import { createTheme } from '@mui/material/styles';

// Define color palette with modern, professional colors
const colors = {
  primary: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2196f3', // Main brand color - Professional blue
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
  },
  secondary: {
    50: '#f3e5f5',
    100: '#e1bee7',
    200: '#ce93d8',
    300: '#ba68c8',
    400: '#ab47bc',
    500: '#9c27b0', // Main secondary color - Professional purple
    600: '#8e24aa',
    700: '#7b1fa2',
    800: '#6a1b9a',
    900: '#4a148c',
  },
  accent: {
    50: '#fff8e1',
    100: '#ffecb3',
    200: '#ffe082',
    300: '#ffd54f',
    400: '#ffca28',
    500: '#ffc107', // Accent color - Professional amber
    600: '#ffb300',
    700: '#ffa000',
    800: '#ff8f00',
    900: '#ff6f00',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  success: {
    50: '#e8f5e9',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50', // Professional green
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
  },
  warning: {
    50: '#fff3e0',
    100: '#ffe0b2',
    200: '#ffcc80',
    300: '#ffb74d',
    400: '#ffa726',
    500: '#ff9800', // Professional orange
    600: '#fb8c00',
    700: '#f57c00',
    800: '#ef6c00',
    900: '#e65100',
  },
  error: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#f44336', // Professional red
    600: '#e53935',
    700: '#d32f2f',
    800: '#c62828',
    900: '#b71c1c',
  },
};

// Light theme configuration with modern design principles
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary[700],
      light: colors.primary[400],
      dark: colors.primary[900],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary[700],
      light: colors.secondary[400],
      dark: colors.secondary[900],
      contrastText: '#ffffff',
    },
    accent: {
      main: colors.accent[700],
      light: colors.accent[400],
      dark: colors.accent[900],
      contrastText: '#000000',
    },
    error: {
      main: colors.error[700],
      light: colors.error[400],
      dark: colors.error[900],
    },
    warning: {
      main: colors.warning[700],
      light: colors.warning[400],
      dark: colors.warning[900],
    },
    success: {
      main: colors.success[700],
      light: colors.success[400],
      dark: colors.success[900],
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
      secondary: colors.neutral[100],
    },
    text: {
      primary: colors.neutral[900],
      secondary: colors.neutral[700],
      disabled: colors.neutral[500],
    },
    divider: colors.neutral[300],
    action: {
      hover: colors.neutral[100],
      selected: colors.primary[50],
      disabled: colors.neutral[400],
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      lineHeight: 1.75,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12, // More rounded corners for modern look
  },
  spacing: 8,
  shadows: [
    'none',
    '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
    '0px 2px 4px 0px rgba(0, 0, 0, 0.05), 0px 2px 6px 0px rgba(0, 0, 0, 0.1)',
    '0px 4px 8px 0px rgba(0, 0, 0, 0.05), 0px 4px 12px 0px rgba(0, 0, 0, 0.1)',
    '0px 6px 12px 0px rgba(0, 0, 0, 0.05), 0px 6px 20px 0px rgba(0, 0, 0, 0.1)',
    '0px 8px 16px 0px rgba(0, 0, 0, 0.05), 0px 8px 24px 0px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px 0px rgba(0, 0, 0, 0.05), 0px 12px 32px 0px rgba(0, 0, 0, 0.1)',
    '0px 16px 32px 0px rgba(0, 0, 0, 0.05), 0px 16px 40px 0px rgba(0, 0, 0, 0.1)',
    '0px 20px 40px 0px rgba(0, 0, 0, 0.05), 0px 20px 48px 0px rgba(0, 0, 0, 0.1)',
    '0px 24px 48px 0px rgba(0, 0, 0, 0.05), 0px 24px 56px 0px rgba(0, 0, 0, 0.1)',
    '0px 28px 56px 0px rgba(0, 0, 0, 0.05), 0px 28px 64px 0px rgba(0, 0, 0, 0.1)',
    '0px 32px 64px 0px rgba(0, 0, 0, 0.05), 0px 32px 72px 0px rgba(0, 0, 0, 0.1)',
    '0px 36px 72px 0px rgba(0, 0, 0, 0.05), 0px 36px 80px 0px rgba(0, 0, 0, 0.1)',
    '0px 40px 80px 0px rgba(0, 0, 0, 0.05), 0px 40px 88px 0px rgba(0, 0, 0, 0.1)',
    '0px 44px 88px 0px rgba(0, 0, 0, 0.05), 0px 44px 96px 0px rgba(0, 0, 0, 0.1)',
    '0px 48px 96px 0px rgba(0, 0, 0, 0.05), 0px 48px 104px 0px rgba(0, 0, 0, 0.1)',
    '0px 52px 104px 0px rgba(0, 0, 0, 0.05), 0px 52px 112px 0px rgba(0, 0, 0, 0.1)',
    '0px 56px 112px 0px rgba(0, 0, 0, 0.05), 0px 56px 120px 0px rgba(0, 0, 0, 0.1)',
    '0px 60px 120px 0px rgba(0, 0, 0, 0.05), 0px 60px 128px 0px rgba(0, 0, 0, 0.1)',
    '0px 64px 128px 0px rgba(0, 0, 0, 0.05), 0px 64px 136px 0px rgba(0, 0, 0, 0.1)',
    '0px 68px 136px 0px rgba(0, 0, 0, 0.05), 0px 68px 144px 0px rgba(0, 0, 0, 0.1)',
    '0px 72px 144px 0px rgba(0, 0, 0, 0.05), 0px 72px 152px 0px rgba(0, 0, 0, 0.1)',
    '0px 76px 152px 0px rgba(0, 0, 0, 0.05), 0px 76px 160px 0px rgba(0, 0, 0, 0.1)',
    '0px 80px 160px 0px rgba(0, 0, 0, 0.05), 0px 80px 168px 0px rgba(0, 0, 0, 0.1)',
    '0px 84px 168px 0px rgba(0, 0, 0, 0.05), 0px 84px 176px 0px rgba(0, 0, 0, 0.1)',
    '0px 88px 176px 0px rgba(0, 0, 0, 0.05), 0px 88px 184px 0px rgba(0, 0, 0, 0.1)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
          },
        },
        outlined: {
          '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '0 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.15)',
            },
          },
        },
      },
    },
  },
});

// Dark theme configuration
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary[400],
      light: colors.primary[300],
      dark: colors.primary[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary[400],
      light: colors.secondary[300],
      dark: colors.secondary[600],
      contrastText: '#ffffff',
    },
    accent: {
      main: colors.accent[400],
      light: colors.accent[300],
      dark: colors.accent[600],
      contrastText: '#000000',
    },
    error: {
      main: colors.error[400],
      light: colors.error[300],
      dark: colors.error[600],
    },
    warning: {
      main: colors.warning[400],
      light: colors.warning[300],
      dark: colors.warning[600],
    },
    success: {
      main: colors.success[400],
      light: colors.success[300],
      dark: colors.success[600],
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
      secondary: colors.neutral[900],
    },
    text: {
      primary: colors.neutral[100],
      secondary: colors.neutral[400],
      disabled: colors.neutral[600],
    },
    divider: colors.neutral[800],
    action: {
      hover: colors.neutral[800],
      selected: colors.primary[900],
      disabled: colors.neutral[700],
    },
  },
  typography: lightTheme.typography,
  shape: lightTheme.shape,
  spacing: lightTheme.spacing,
  components: {
    ...lightTheme.components,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backgroundImage: 'none',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.4)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(30, 30, 30, 0.85)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.2)',
          backgroundImage: 'none',
        },
      },
    },
  },
});

export { lightTheme, darkTheme, colors };